import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectPupCatalog } from '../../redux/pupCatalogSlice';
import { formatLogEvent, cardSegments } from '../../redux/actionLogFormatter';
import { formatAmount } from '../../utilities/chips';
import { fetchWithToken, postWithToken } from '../../utilities/fetchWithToken';
import { BASE_URL } from '../../utilities/BASE_URL';
import { foldBoardState } from './replayBoard';
import LogLine from './LogLine';
import PupCardDetail from './PupCardDetail';
import './ReplayWindow.css';

// A replay of a table's PREVIOUS completed hand, occupying one screen slot. It fetches the hand's
// server-filtered event array (GET /handhistory?hand=previous), renders it through the SHARED
// actionLogFormatter (no second formatter), and derives a lightweight board/pot strip by folding
// events up to the current step (no full table re-render in v1). Playback steps through the events;
// a bug can be reported for the hand. All hidden information stays server-side — this only shows
// events the server already delivered to this caller.

const STEP_MS = 1500;       // 1x auto-advance interval per step
const MAX_COMMENT = 500;    // matches the server-side limit (advisory client-side)
const CONFIRM_MS = 1600;    // how long the "submitted" confirmation shows before the box closes

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

const ReplayWindow = ({ roomId, onExit }) => {
  const catalog = useSelector(selectPupCatalog);

  const [events, setEvents] = useState(null); // null=loading, []=none/empty, [...]=loaded
  const [loadError, setLoadError] = useState(null);
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [speed, setSpeed] = useState(1); // 1 or 2
  const [detailCode, setDetailCode] = useState(null);

  // Bug report
  const [reportOpen, setReportOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [reported, setReported] = useState(false); // disables the button (submitted OR already reported)
  const [reportMessage, setReportMessage] = useState(null); // error/info shown in the box
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const rootRef = useRef(null);
  const listBottomRef = useRef(null);

  // Fetch the previous hand once per table.
  useEffect(() => {
    let cancelled = false;
    setEvents(null);
    setLoadError(null);
    fetchWithToken(`${BASE_URL}/games/${roomId}/handhistory?hand=previous`)
      .then(async (res) => {
        if (cancelled) return;
        if (res.status === 404) { setEvents([]); return; }
        if (!res.ok) throw new Error(`handhistory ${res.status}`);
        const data = await res.json();
        if (!cancelled) setEvents(Array.isArray(data) ? data : []);
      })
      .catch(() => { if (!cancelled) setLoadError('Could not load the last hand.'); });
    return () => { cancelled = true; };
  }, [roomId]);

  // Focus on mount so Esc / arrow keys work immediately without a click.
  useEffect(() => { rootRef.current?.focus?.(); }, []);

  const total = events ? events.length : 0;
  const lastIndex = Math.max(0, total - 1);

  // Keep the step in range once events load.
  useEffect(() => { setStep((s) => Math.min(s, lastIndex)); }, [lastIndex]);

  const goPrev = useCallback(() => setStep((s) => Math.max(0, s - 1)), []);
  const goNext = useCallback(() => setStep((s) => Math.min(lastIndex, s + 1)), [lastIndex]);

  // Auto-advance: schedule the next step while playing and not yet at the end. Reduce-motion only
  // affects the log's scroll animation (below); the step jumps are already discrete.
  useEffect(() => {
    if (!playing || total === 0 || step >= lastIndex) return undefined;
    const id = setTimeout(() => setStep((s) => Math.min(s + 1, lastIndex)), STEP_MS / speed);
    return () => clearTimeout(id);
  }, [playing, step, speed, total, lastIndex]);

  // Stop auto-advancing once the end is reached.
  useEffect(() => { if (total > 0 && step >= lastIndex) setPlaying(false); }, [step, lastIndex, total]);

  // Resolver context for the SHARED formatter. Names come from the past hand's HAND_START roster
  // (players may have changed since); the viewer seat is the one whose private hole cards the
  // server delivered to us (so "Your …" lines are ours, and never another seat's).
  const ctx = useMemo(() => {
    const start = events && events.find((e) => e.type === 'HAND_START');
    const seatNames = {};
    ((start && start.payload && start.payload.seats) || []).forEach((s) => {
      if (s && s.seat != null) seatNames[s.seat] = s.username;
    });
    const mine = events && events.find((e) => e.type === 'HOLE_CARDS_DEALT' && e.actorSeat != null);
    return {
      resolveSeatName: (seat) => seatNames[seat] || `Seat ${seat}`,
      resolvePupName: (code) => (catalog && catalog[code] && catalog[code].name) || code,
      viewerSeat: mine ? mine.actorSeat : null,
    };
  }, [events, catalog]);

  // Formatted lines revealed up to the current step.
  const lines = useMemo(() => {
    if (!events || total === 0) return [];
    const out = [];
    const end = Math.min(step, lastIndex);
    for (let i = 0; i <= end; i++) {
      const formatted = formatLogEvent(events[i], ctx);
      if (formatted == null) continue;
      (Array.isArray(formatted) ? formatted : [formatted]).forEach((l) => out.push(l));
    }
    return out;
  }, [events, step, lastIndex, total, ctx]);

  const { board, pot } = useMemo(() => foldBoardState(events, step), [events, step]);

  // Auto-scroll the revealed log to the newest line (jump instead of smooth under reduce-motion).
  useEffect(() => {
    listBottomRef.current?.scrollIntoView?.({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'end' });
  }, [lines]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); onExit(); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev(); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); goNext(); }
  };

  const submitReport = async () => {
    setSubmitting(true);
    setReportMessage(null);
    try {
      const res = await postWithToken(`${BASE_URL}/games/${roomId}/bugreport`, {
        hand: 'previous',
        comment: comment.slice(0, MAX_COMMENT),
      });
      if (res.ok) {
        setReported(true);
        setConfirming(true);
        setTimeout(() => { setConfirming(false); setReportOpen(false); }, CONFIRM_MS);
      } else {
        const text = await res.text().catch(() => '');
        if (/already reported/i.test(text)) {
          // One report per player per hand — treat exactly like a successful submit's disabled state.
          setReported(true);
          setReportMessage('You have already reported this hand.');
        } else {
          setReportMessage(text || 'Could not submit the report.');
        }
      }
    } catch (_) {
      setReportMessage('Could not submit the report.');
    } finally {
      setSubmitting(false);
    }
  };

  const stepLabel = total ? `${Math.min(step, lastIndex) + 1} / ${total}` : '0 / 0';

  return (
    <div className="replay-window" ref={rootRef} tabIndex={0} onKeyDown={onKeyDown} aria-label="Hand replay">
      <div className="replay-topright">
        <button
          type="button"
          className="replay-report-btn"
          onClick={() => { setReportOpen(true); setReportMessage(null); }}
          disabled={reported || (events != null && total === 0)}
          title={reported ? 'You have already reported this hand' : 'Report a bug in this hand'}
        >
          Report bug
        </button>
        <button type="button" className="replay-exit-btn" onClick={onExit} title="Exit replay (Esc)">
          Exit
        </button>
      </div>

      <div className="replay-header">
        <span className="replay-title">Replay — Last Hand</span>
      </div>

      <div className="replay-board" aria-label="Board and pot">
        <div className="replay-board-cards">
          {board.length === 0
            ? <span className="replay-board-empty">No board yet</span>
            : cardSegments(board.join(' ')).map((c, i) => (
                <span key={i} className="replay-card" style={{ color: c.color }}>{c.text}</span>
              ))}
        </div>
        <div className="replay-board-pot">Pot: {formatAmount(pot)}</div>
      </div>

      <div className="replay-log">
        {events == null && !loadError && <div className="replay-empty">Loading…</div>}
        {loadError && <div className="replay-empty">{loadError}</div>}
        {events != null && total === 0 && !loadError && (
          <div className="replay-empty">No completed hand to replay yet.</div>
        )}
        {lines.map((line) => (
          <LogLine key={line.key} line={line} onPupClick={setDetailCode} roomId={roomId} />
        ))}
        <div ref={listBottomRef} />
      </div>

      <div className="replay-controls">
        <button type="button" onClick={goPrev} disabled={step <= 0} title="Rewind (←)" aria-label="Rewind">⏮</button>
        <button
          type="button"
          onClick={() => setPlaying((p) => !p)}
          disabled={total === 0}
          title={playing ? 'Pause' : 'Play'}
          aria-label={playing ? 'Pause' : 'Play'}
        >
          {playing ? '⏸' : '▶'}
        </button>
        <button type="button" onClick={goNext} disabled={step >= lastIndex} title="Fast-forward (→)" aria-label="Fast-forward">⏭</button>
        <button
          type="button"
          className={`replay-speed ${speed === 2 ? 'active' : ''}`}
          onClick={() => setSpeed((s) => (s === 2 ? 1 : 2))}
          title="Toggle 2× speed"
          aria-label="Toggle double speed"
          aria-pressed={speed === 2}
        >
          2×
        </button>
        <span className="replay-step">{stepLabel}</span>
      </div>

      {reportOpen && (
        <div className="replay-report-overlay" onClick={() => !submitting && setReportOpen(false)}>
          <div
            className="replay-report-box"
            role="dialog"
            aria-modal="true"
            aria-label="Report a bug"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="replay-report-title">Report a bug</h3>
            {confirming ? (
              <p className="replay-report-confirm">Thanks — your report was submitted.</p>
            ) : reported ? (
              <>
                <p className="replay-report-confirm">{reportMessage || 'This hand has been reported.'}</p>
                <div className="replay-report-actions">
                  <button type="button" onClick={() => setReportOpen(false)}>Close</button>
                </div>
              </>
            ) : (
              <>
                <textarea
                  className="replay-report-input"
                  value={comment}
                  maxLength={MAX_COMMENT}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Describe what went wrong in this hand…"
                  aria-label="Bug description"
                />
                <div className="replay-report-count">{comment.length} / {MAX_COMMENT}</div>
                {reportMessage && <div className="replay-report-error">{reportMessage}</div>}
                <div className="replay-report-actions">
                  <button type="button" onClick={() => setReportOpen(false)} disabled={submitting}>Cancel</button>
                  <button
                    type="button"
                    onClick={submitReport}
                    disabled={submitting || comment.trim().length === 0}
                  >
                    Submit
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {detailCode && <PupCardDetail code={detailCode} roomId={roomId} onClose={() => setDetailCode(null)} />}
    </div>
  );
};

export default ReplayWindow;
